import { FC, ReactNode, createContext, use, useCallback, useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'

interface ContextValue {
    show: (Node: ReactNode) => void
    hide: () => void
}

interface ProviderProps {
    children: ReactNode
}

export const createPortal = () => {
    const Context = createContext<ContextValue>({
        show: () => {},
        hide: () => {},
    })

    const Provider: FC<ProviderProps> = ({ children }) => {
        const [Node, setNode] = useState<ReactNode>(null)

        const show = useCallback((Node: ReactNode) => setNode(Node), [])
        const hide = useCallback(() => setNode(null), [])

        const values = useMemo(() => ({ show, hide }), [show, hide])

        return (
            <Context.Provider value={values}>
                <View style={{ flex: 1 }}>
                    {children}
                    {Node}
                </View>
            </Context.Provider>
        )
    }

    const usePortal = (isVisible: boolean, Node: ReactNode): void => {
        const { show, hide } = use(Context)

        useEffect(() => {
            if (isVisible) return show(Node)

            hide()
        }, [isVisible, Node, show, hide])

        useEffect(() => {
            return hide
        }, [hide])
    }

    return { Provider, usePortal }
}
